import { ArrowUpRight, Clock, Dumbbell, Mail, MapPin, Phone } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { DotRing } from "../../components/dot-ring";
import { EmptyCard } from "../../components/empty-card";
import { card, inkTag, label, orb, primaryCard } from "../../components/section";
import { PageTitle, SiteHead } from "../../components/site-head";
import { SitePage } from "../../components/site-page";
import { usePublicDirectory } from "../../hooks/use-public-directory";
import { useSiteConfig, type SiteConfig } from "../../hooks/use-site-config";
import type { PublicPlace } from "../../types";
import { placeName } from "../../utils/directory";
import { openUntil } from "../../utils/opening-hours";
import { Team } from "../team/team";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

type Channel = { icon: LucideIcon; label: string; value: string; note?: string; href: string; external?: boolean };

/** A place as a card: its gym or name, its street, and a maps search that finds the door. */
const placeChannel = (place: PublicPlace): Channel => {
  const street = [place.address, place.city && !place.address.includes(place.city) ? place.city : ""]
    .filter(Boolean)
    .join(", ");
  return {
    icon: place.gym ? Dumbbell : MapPin,
    label: place.gym ? m.contact_label_gym() : m.contact_label_address(),
    value: placeName(place),
    note: placeName(place) === place.address ? undefined : street,
    href:
      place.mapsUrl ??
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([placeName(place), street].filter(Boolean).join(", "))}`,
    external: true,
  };
};

/**
 * The ways to reach the trainer they filled in, and only those, then every place their coaches
 * work. The address typed on the page stands in only when the calendar knows no place.
 */
const channels = (site: SiteConfig, places: PublicPlace[]): Channel[] => [
  ...[
    { icon: Phone, label: m.contact_label_phone(), value: site.phone, href: site.phoneHref },
    { icon: Mail, label: m.contact_label_email(), value: site.email, href: `mailto:${site.email}` },
    ...(places.length === 0
      ? [{ icon: MapPin, label: m.contact_label_address(), value: site.address, href: site.mapUrl, external: true }]
      : []),
  ].filter((channel) => channel.value),
  ...places.map(placeChannel),
];

const hours = (site: SiteConfig) => [
  { day: m.contact_hours_weekdays(), value: site.hours.weekdays || null },
  { day: m.contact_hours_saturday(), value: site.hours.saturday },
  { day: m.contact_hours_sunday(), value: site.hours.sunday },
];

/** Days of the week the studio opens, Monday first: one dot each on the hours card's ring. */
const openDays = (site: SiteConfig) => {
  const { weekdays, saturday, sunday } = site.hours;
  return (weekdays ? 5 : 0) + (saturday ? 1 : 0) + (sunday ? 1 : 0);
};

/**
 * How to reach the trainer. Every fact comes from their page settings; a fact they left empty is
 * not on the page, and the opening hours card only shows once they gave any hours.
 */
export const Contact = () => {
  const site = useSiteConfig();
  const { directory } = usePublicDirectory({ days: site.calendarDays });
  const ways = channels(site, directory?.places ?? []);
  const days = openDays(site);
  const closes = openUntil(site.hours, new Date(), directory?.business.timeZone);

  return (
    <SitePage
      head={
        <SiteHead>
          <PageTitle title={m.landing_nav_contact()} aside={ways.length > 0 ? m.contact_aside() : undefined} />
        </SiteHead>
      }
    >
      {ways.length === 0 && days === 0 ? (
        <EmptyCard title={m.contact_empty_title()} body={m.contact_empty_body()} />
      ) : (
        <div className={cn("mt-3 grid gap-3", ways.length > 0 && days > 0 && "lg:grid-cols-2")}>
          {ways.length > 0 ? (
            <div className="grid content-start gap-3">
              {ways.map(({ icon: Icon, label: title, value, note, href, external }) => (
                <a
                  key={title}
                  href={href}
                  {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
                  className={cn(
                    card,
                    "group grid grid-cols-[auto_1fr_auto] items-center gap-4 p-4 outline-none focus-visible:ring-2 focus-visible:ring-ring md:p-4",
                  )}
                >
                  <span className="grid size-12 place-items-center rounded-2xl bg-primary/15 text-[var(--site-ink)]">
                    <Icon aria-hidden className="size-[18px]" />
                  </span>
                  <div className="min-w-0">
                    <small className={cn(label, "block")}>{title}</small>
                    <b className="block text-lg font-medium tracking-[-0.02em] break-words sm:text-xl">{value}</b>
                    {note ? <small className={cn(label, "mt-0.5 block")}>{note}</small> : null}
                  </div>
                  <span className={cn(orb, "group-hover:bg-primary group-hover:text-primary-foreground")}>
                    <ArrowUpRight aria-hidden className="size-[18px]" />
                  </span>
                </a>
              ))}
            </div>
          ) : null}

          {days > 0 ? (
            <div className={cn(primaryCard, "self-start")}>
              <div className="flex items-center justify-between gap-4">
                <span className={cn(inkTag, "self-center")}>
                  <Clock aria-hidden className="size-3.5" />
                  {closes ? m.contact_open_now({ time: closes }) : m.contact_closed_now()}
                </span>
                <DotRing filled={days} total={7} dot={6} tone="primary" className="size-12">
                  <span className="text-[13px] font-semibold tabular-nums">{days}</span>
                </DotRing>
              </div>
              <dl className="mt-4">
                {hours(site).map((row) => (
                  <div
                    key={row.day}
                    className="flex justify-between gap-4 border-t border-[var(--site-on-primary-dot)] py-3 last:pb-0"
                  >
                    <dt>{row.day}</dt>
                    <dd className={row.value ? "font-semibold tabular-nums" : "text-[var(--site-on-primary-soft)]"}>
                      {row.value ?? m.contact_closed()}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}
        </div>
      )}

      <Team directory={directory} />
    </SitePage>
  );
};
